import { ref, computed } from 'vue';
import api from '@/services/api';
import type { SchoolTypeCategory } from '@/types/feature_access_schema';

// Feature access state
const features = ref<Record<string, boolean>>({});
const subscriptionFeatures = ref<string[]>([]);
const addonFeatures = ref<string[]>([]);
const schoolTypes = ref<SchoolTypeCategory[]>([]);  // School types from API
// The backend is the single source of truth for type restrictions. It returns
// this list even when a feature is not included in the school's plan so the UI
// can distinguish “not applicable” from “upgrade required”.
const schoolTypeIneligibleFeatures = ref<Set<string>>(new Set());
const loading = ref(false);
const error = ref<string | null>(null);
const lastFetched = ref<Date | null>(null);

// Cache duration - 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Feature Access Composable
 * Manages feature checking and access control based on subscription plans and addons
 */
export const useFeatureAccess = () => {

  /**
   * Check if a feature is available to the current school
   */
  const hasFeature = (featureCode: string): boolean => {
    return features.value[featureCode] === true;
  };

  /**
   * Check multiple features at once
   */
  const hasFeatures = (featureCodes: string[]): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    featureCodes.forEach(code => {
      result[code] = hasFeature(code);
    });
    return result;
  };

  /**
   * Require a feature - returns true if available, false if not
   */
  const requireFeature = (featureCode: string): boolean => {
    return hasFeature(featureCode);
  };

  /**
   * Check if feature data needs refresh
   */
  const needsRefresh = computed(() => {
    if (!lastFetched.value) return true;
    return Date.now() - lastFetched.value.getTime() > CACHE_DURATION;
  });

  /**
   * Fetch features from the API
   */
  const fetchFeatures = async (forceRefresh = false) => {
    // Skip if we have recent data and not forcing refresh
    if (!forceRefresh && !needsRefresh.value && Object.keys(features.value).length > 0) {
      return features.value;
    }

    loading.value = true;
    error.value = null;

    try {
      const response = await api.get('/school/features/access');

      if (response.data && response.data.data) {
        const data = response.data.data;

        // Handle both documented format (arrays) and legacy format (boolean map)
        if (data.features && Array.isArray(data.features)) {
          const featureMap: Record<string, boolean> = {};
          data.features.forEach((feature: string) => {
            featureMap[feature] = true;
          });
          features.value = featureMap;

          // Store additional metadata
          subscriptionFeatures.value = data.subscription_features || [];
          addonFeatures.value = data.addon_features || [];
          schoolTypeIneligibleFeatures.value = new Set(
            Array.isArray(data.school_type_ineligible_features)
              ? data.school_type_ineligible_features.filter((code: unknown): code is string => typeof code === 'string')
              : []
          );
          schoolTypes.value = (data.school_types || []).map((schoolType: string) => {
            if (['elementary_school', 'middle_school', 'high_school'].includes(schoolType)) {
              return 'k12';
            }
            if (['college', 'university'].includes(schoolType)) {
              return 'university';
            }
            return schoolType;
          }).filter((schoolType: string): schoolType is SchoolTypeCategory =>
            ['k12', 'university', 'course_base'].includes(schoolType)
          );
        } else if (data.features && typeof data.features === 'object') {
          // Legacy format: {"features": {"feature1": true, "feature2": false}}
          features.value = data.features;
          subscriptionFeatures.value = [];
          addonFeatures.value = [];
          schoolTypeIneligibleFeatures.value = new Set();
        } else {
          throw new Error('Invalid response format from features available API');
        }

        lastFetched.value = new Date();
      } else {
        throw new Error('Invalid response format from features available API');
      }

      return features.value;
    } catch (err) {
      console.error('Failed to fetch features:', err);
      error.value = err instanceof Error ? err.message : 'Failed to fetch available features';

      // Return empty features object on error
      features.value = {};
      subscriptionFeatures.value = [];
      addonFeatures.value = [];
      schoolTypeIneligibleFeatures.value = new Set();
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Check a single feature from the API
   */
  const checkSingleFeature = async (featureCode: string) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get(`/school/features/check?feature=${featureCode}`);

      if (response.data && response.data.data && typeof response.data.data.has_access === 'boolean') {
        // Update our local cache with this single feature result
        features.value[featureCode] = response.data.data.has_access;
        lastFetched.value = new Date();

        return response.data.data.has_access;
      } else {
        throw new Error('Invalid response format from single feature check API');
      }
    } catch (err) {
      console.error('Failed to check single feature:', err);
      error.value = err instanceof Error ? err.message : 'Failed to check feature';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Bulk check specific features
   */
  const bulkCheckFeatures = async (featureCodes: string[]) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/school/features/bulk-check', {
        features: featureCodes
      });

      if (response.data && response.data.data && response.data.data.features) {
        // Update our local features cache with the results
        Object.assign(features.value, response.data.data.features);
        lastFetched.value = new Date();

        return response.data.data.features;
      } else {
        throw new Error('Invalid response format from bulk features API');
      }
    } catch (err) {
      console.error('Failed to bulk check features:', err);
      error.value = err instanceof Error ? err.message : 'Failed to check features';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Clear the features cache
   */
  const clearCache = () => {
    features.value = {};
    subscriptionFeatures.value = [];
    addonFeatures.value = [];
    schoolTypeIneligibleFeatures.value = new Set();
    lastFetched.value = null;
    error.value = null;
  };

  /**
   * Get feature information (name, description, etc.)
   */
  const getFeatureInfo = (featureCode: string) => {
    // This could be enhanced to return feature metadata
    // For now, just return basic info
    return {
      code: featureCode,
      available: hasFeature(featureCode),
      name: featureCode.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    };
  };

  /**
   * Check if any of the provided features are available
   */
  const hasAnyFeature = (featureCodes: string[]): boolean => {
    return featureCodes.some(code => hasFeature(code));
  };

  /**
   * Check if all of the provided features are available
   */
  const hasAllFeatures = (featureCodes: string[]): boolean => {
    return featureCodes.every(code => hasFeature(code));
  };

  /**
   * Get list of available features
   */
  const getAvailableFeatures = computed(() => {
    return Object.keys(features.value).filter(code => features.value[code]);
  });

  /**
   * Get list of unavailable features from a given list
   */
  const getUnavailableFeatures = (featureCodes: string[]): string[] => {
    return featureCodes.filter(code => !hasFeature(code));
  };

  /**
   * Get subscription features
   */
  const getSubscriptionFeatures = computed(() => subscriptionFeatures.value);

  /**
   * Get addon features
   */
  const getAddonFeatures = computed(() => addonFeatures.value);

  /**
   * Check if a feature comes from subscription plan
   */
  const isSubscriptionFeature = (featureCode: string): boolean => {
    return subscriptionFeatures.value.includes(featureCode);
  };

  /**
   * Check if a feature comes from addons
   */
  const isAddonFeature = (featureCode: string): boolean => {
    return addonFeatures.value.includes(featureCode);
  };

  // ===== SCHOOL TYPE CHECKING =====

  /**
   * Get current school types from API response
   * Returns the school type categories this school belongs to
   */
  const currentSchoolTypes = computed((): SchoolTypeCategory[] => {
    return schoolTypes.value;
  });

  /**
   * Check if a feature is available for the current school type. The backend
   * resolves this from feature_school_types, including multi-type schools.
   * Returns true if the feature should be VISIBLE for this school type
   */
  const isAvailableForSchoolType = (featureCode: string): boolean => {
    return !schoolTypeIneligibleFeatures.value.has(featureCode);
  };

  /**
   * Check if the backend marked a feature as unavailable for this school type.
   */
  const needsSchoolTypeFilter = (featureCode: string): boolean => {
    return schoolTypeIneligibleFeatures.value.has(featureCode);
  };

  /**
   * Combined check: School type + Subscription
   * Use this for determining if a menu item should be SHOWN
   *
   * @returns 'hidden' - completely hide (wrong school type)
   * @returns 'upgrade' - show but needs upgrade (subscription issue)
   * @returns 'allowed' - full access
   */
  const getFeatureAccessLevel = (featureCode: string): 'hidden' | 'upgrade' | 'allowed' => {
    // First check school type - if not available, hide completely
    if (!isAvailableForSchoolType(featureCode)) {
      return 'hidden';
    }

    // Then check subscription/addon features
    if (!hasFeature(featureCode)) {
      return 'upgrade';
    }

    return 'allowed';
  };

  /**
   * Check if feature should be completely hidden (school type mismatch)
   */
  const shouldHideFeature = (featureCode: string): boolean => {
    return getFeatureAccessLevel(featureCode) === 'hidden';
  };

  /**
   * Check if feature should show upgrade prompt (subscription issue)
   */
  const shouldShowUpgrade = (featureCode: string): boolean => {
    return getFeatureAccessLevel(featureCode) === 'upgrade';
  };

  return {
    // State
    features: computed(() => features.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    lastFetched: computed(() => lastFetched.value),
    needsRefresh,
    currentSchoolTypes,

    // School type convenience helpers
    hasK12: computed(() => schoolTypes.value.includes('k12')),
    hasUniversity: computed(() => schoolTypes.value.includes('university')),
    hasCourseBase: computed(() => schoolTypes.value.includes('course_base')),
    isMultiType: computed(() => schoolTypes.value.length > 1),

    // Enrollment mode (mirrors backend services.GetEnrollmentMode):
    // university / course_base enroll per course section; K-12 enrolls by class.
    // Section mode wins for multi-type schools (it's the granular superset).
    usesSectionEnrollment: computed(() =>
      schoolTypes.value.includes('university') || schoolTypes.value.includes('course_base')
    ),

    // Methods
    hasFeature,
    hasFeatures,
    requireFeature,
    hasAnyFeature,
    hasAllFeatures,
    fetchFeatures,
    checkSingleFeature,
    bulkCheckFeatures,
    clearCache,
    getFeatureInfo,
    getAvailableFeatures,
    getUnavailableFeatures,
    getSubscriptionFeatures,
    getAddonFeatures,
    isSubscriptionFeature,
    isAddonFeature,

    // School type methods
    isAvailableForSchoolType,
    needsSchoolTypeFilter,
    getFeatureAccessLevel,
    shouldHideFeature,
    shouldShowUpgrade
  };
};

// Export a singleton instance for global state management
export default useFeatureAccess;
